import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { OpenFeature } from '@openfeature/react-sdk'

import Api from '../utils/api'
import { AUTH_TOKEN, AUTH_TOKEN_VALUE, LOGOUT_TOKEN, VERIFY_FOLDER_ID, VERIFY_NODE_ID } from '../utils/constants'
import useSessionMonitor from '../utils/hooks/useSessionMonitor'

import { ChatContext } from './chat-provider'

export const AuthenticationContext = createContext<{
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>
  isAuthenticated: boolean
  login: string
  setLogin: Dispatch<SetStateAction<string>>
  ownUser: IUserType | undefined
  setOwnUser: Dispatch<SetStateAction<IUserType | undefined>>
  ownPermissions: IPermissionType[]
  handleLogout: () => Promise<void>
}>({
  setIsAuthenticated: () => null,
  isAuthenticated: false,
  login: '',
  setLogin: () => null,
  ownUser: undefined,
  ownPermissions: [],
  setOwnUser: () => null,
  handleLogout: async () => {},
})

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const { endChat } = useContext(ChatContext)
  const [ownUser, setOwnUser] = useState<IUserType | undefined>(undefined)
  const [ownPermissions, setOwnPermissions] = useState<IPermissionType[]>([])

  const [login, setLogin] = useState<string>('')

  // guard the access with try/catch to prevent Sentry SecurityError for Safari users
  // which was thrown when we did const [isAuthenticated, setIsAuthenticated] = useState<boolean>(localStorage?.getItem(AUTH_TOKEN) === AUTH_TOKEN_VALUE)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTH_TOKEN) === AUTH_TOKEN_VALUE
    } catch {
      return false
    }
  })

  useEffect(() => {
    let isSubscribed = true

    const getOwnUser = async () => {
      try {
        const res = await Api.get('/api/users/me')

        if (res.ok) {
          setIsAuthenticated(true)
          setOwnUser(res.body)
          localStorage.setItem(AUTH_TOKEN, AUTH_TOKEN_VALUE)

          // Set feature flag context with user information after login
          const evaluationContext = {
            targetingKey: String(res.body.id),
            userId: String(res.body.id),
            roleId: String(res.body.roleId),
          }
          OpenFeature.setContext(evaluationContext)

          const resPermissions = await Api.get('/api/permissions?onlyGranted=true')
          if (resPermissions.ok) {
            const permissions = resPermissions.body
            setOwnPermissions(permissions)
          }
        } else {
          setIsAuthenticated(false)
          localStorage.removeItem(AUTH_TOKEN)
        }
      } catch {
        if (isSubscribed) {
          setIsAuthenticated(false)
          localStorage.removeItem(AUTH_TOKEN)
        }
      }
    }

    // reset ownUser on logout
    if (!isAuthenticated && ownUser) {
      setOwnUser(undefined)
    }

    if (isAuthenticated && !ownUser) {
      getOwnUser()
    }

    return () => {
      isSubscribed = false
    }
  }, [isAuthenticated, ownUser])

  const handleLogout = useCallback(
    async (fromInterceptor = false) => {
      if (!fromInterceptor) await Api.get('/api/logout')

      setIsAuthenticated(false)
      setOwnPermissions([])
      endChat()

      localStorage.setItem(LOGOUT_TOKEN, 'true')

      localStorage.removeItem(AUTH_TOKEN)
      localStorage.removeItem(VERIFY_FOLDER_ID)
      localStorage.removeItem(VERIFY_NODE_ID)
    },
    [endChat]
  )

  // Override window.fetch to handle 401 responses
  useEffect(() => {
    const originalFetch = window.fetch
    window.fetch = async (...args) => {
      const response = await originalFetch(...args)
      if (response.status === 401) {
        handleLogout(true)
      }
      return response
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [handleLogout])

  // Ping `/api/users/me` every minute to verify the session is still valid. Once the server session expires, this ping will return a 401 and the user will be logged out on the front end and redirected to the login screen.
  useSessionMonitor({
    isAuthenticated,
    checkIntervalMs: 60000, // Check every 60 seconds
    enabled: isAuthenticated && !!ownUser,
  })

  return (
    <AuthenticationContext.Provider value={{ setIsAuthenticated, isAuthenticated, login, setLogin, ownUser, setOwnUser, handleLogout, ownPermissions }}>
      {children}
    </AuthenticationContext.Provider>
  )
}

export default AuthProvider
